
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { SmilePlus, BarChart2, TrendingUp, Info, Check, AlertTriangle, ThumbsUp, ThumbsDown } from 'lucide-react';

const CustomerSatisfaction = ({ segmentId, satisfactionData }) => {
  const segments = [
    { id: 'urban', name: 'Urban Premium' },
    { id: 'highway', name: 'Highway Transit' },
    { id: 'rural', name: 'Rural Standard' },
    { id: 'suburban', name: 'Suburban Mixed' }
  ];

  const data = segments.map(segment => ({
    name: segment.name,
    satisfaction: satisfactionData[segment.id] || 80,
    fill: segment.id === segmentId ? '#A100FF' : '#D6ABFF'
  }));

  // Determine the satisfaction status - fixed to use consistent criteria
  const getSatisfactionStatus = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 85) return 'Good';
    if (score >= 80) return 'Satisfactory';
    return 'Needs Improvement';
  };

  // Current segment satisfaction - ensure it's a stable value
  const currentSegmentSatisfaction = satisfactionData[segmentId] || 80;
  const status = getSatisfactionStatus(currentSegmentSatisfaction);

  // Get appropriate color for the status
  const getStatusColor = (status) => {
    switch(status) {
      case 'Excellent': return 'text-green-600';
      case 'Good': return 'text-blue-600';
      case 'Satisfactory': return 'text-yellow-600';
      default: return 'text-red-600';
    }
  };

  // Calculate trend with stable, fixed value instead of random
  const getTrendIndicator = () => {
    // Fixed trend value based on segment ID for stability
    const trendMap = {
      'urban': 2.3,
      'highway': -1.7,
      'rural': 1.5,
      'suburban': -0.8
    };
    
    const trendValue = trendMap[segmentId] || 0;
    return {
      value: trendValue,
      isPositive: trendValue > 0
    };
  };
  
  const trend = getTrendIndicator();

  // Get appropriate icon for the status
  const getStatusIcon = (status) => {
    switch(status) {
      case 'Excellent': return <Check size={14} className="mr-1 text-green-600" />;
      case 'Good': return <ThumbsUp size={14} className="mr-1 text-blue-600" />;
      case 'Satisfactory': return <Info size={14} className="mr-1 text-yellow-600" />;
      default: return <AlertTriangle size={14} className="mr-1 text-red-600" />;
    }
  };

  // Get satisfaction benchmark by segment - fixed values
  const getSegmentBenchmark = (segmentId) => {
    const benchmarks = {
      urban: 92,
      highway: 85,
      rural: 88,
      suburban: 86
    };
    return benchmarks[segmentId] || 85;
  };

  const benchmark = getSegmentBenchmark(segmentId);

  // Get action recommendations based on satisfaction
  const getActionRecommendations = (score, benchmark) => {
    if (score >= benchmark) return "Maintain current service levels and identify success factors for replication.";
    if (score >= benchmark - 5) return "Minor improvements needed, focus on staff training and facility cleanliness.";
    if (score >= benchmark - 10) return "Moderate improvements required, review customer feedback for specific issues.";
    return "Urgent action needed. Comprehensive service quality review recommended.";
  };

  const recommendations = getActionRecommendations(currentSegmentSatisfaction, benchmark);

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="mb-4 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <SmilePlus size={20} className="mr-2 text-purple-600" />
          Customer Satisfaction
        </h3>
        <div className="flex items-center space-x-2">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            status === 'Excellent' ? 'bg-green-100 text-green-800' :
            status === 'Good' ? 'bg-blue-100 text-blue-800' :
            status === 'Satisfactory' ? 'bg-yellow-100 text-yellow-800' :
            'bg-red-100 text-red-800'
          }`}>
            {status}
          </span>
          <div className={`flex items-center text-xs font-medium ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
            <TrendingUp size={14} className={`mr-1 ${trend.isPositive ? '' : 'transform rotate-180'}`} />
            {trend.isPositive ? '+' : ''}{trend.value.toFixed(1)}%
          </div>
        </div>
      </div>
      
      {/* Reduced height of the chart container from h-64 to h-48 */}
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            {/* Show all segment names on x-axis by setting interval to 0 */}
            <XAxis dataKey="name" interval={0} tick={{ fontSize: 10 }} />
            <YAxis domain={[70, 100]} />
            <Tooltip formatter={(value) => `${value}%`} />
            <Bar dataKey="satisfaction" fill={(entry) => entry.fill} radius={[4, 4, 0, 0]}>
              <LabelList dataKey="satisfaction" position="top" formatter={(value) => `${value}%`} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4 pt-3 border-t border-gray-100">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center">
            <h4 className="text-sm font-medium text-gray-700">
              {segments.find(s => s.id === segmentId)?.name} Satisfaction:
            </h4>
            {getStatusIcon(status)}
          </div>
          <span className={`text-lg font-semibold ${getStatusColor(status)}`}>
            {currentSegmentSatisfaction}%
          </span>
        </div>
        
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">Status:</span>
          <span className={`text-sm font-medium ${getStatusColor(status)}`}>
            {status}
          </span>
        </div>
        
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">Segment Benchmark:</span>
          <span className="text-sm font-medium text-purple-700">
            {benchmark}%
          </span>
        </div>
        
        <div className="mt-3 text-sm text-gray-600">
          <div className="mb-2 font-medium">Analysis:</div>
          {status === 'Excellent' && 
            'Customer experience is exceptional, leading to high loyalty and repeat business.'}
          {status === 'Good' && 
            'Most customers are satisfied, with some opportunities for improvement.'}
          {status === 'Satisfactory' && 
            'Basic needs are met, but there are significant improvement opportunities.'}
          {status === 'Needs Improvement' && 
            'Urgent attention required to address customer experience issues.'}
          
          <div className="mt-2 font-medium">Recommended Actions:</div>
          <div className="text-sm text-gray-700">{recommendations}</div>
          
          <div className="mt-3 text-sm text-purple-700 flex items-center">
            <Info size={14} className="mr-1" />
            Data source: Customer surveys and feedback analysis
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSatisfaction;
